import { FC, useState, useRef, KeyboardEvent, ReactChild } from "react";

import cx from "classnames";
import List from "./List";

import styles from "./styles.module.scss";
import Icon, { IconTypes } from "../../../../../../../../libs/Icon";

export enum LogicSelectMode {
    DEFAULT,
    CHECKBOX,
    RADIO,
}
export interface IOptions {
    [key: string]: string;
}

export type IOnChange = (handleChange: Function) => (key: IOptions["key"], index: number) => void;

export interface ICustoms {
    [key: string]: (onChange: (key: IOptions["key"], index: number) => void, key: IOptions["key"]) => ReactChild;
}

interface IProps {
    options: IOptions;
    initial?: string | null;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    useListWithPortal?: boolean;
    valueKey?: string;
    handleChange: (newValue: string) => void;
    selectMode?: LogicSelectMode;
}

const LogicStateSelect: FC<IProps> = ({
    options,
    initial,
    placeholder = "",
    className,
    disabled,
    valueKey,
    handleChange,
}) => {
    const initialIndex = initial ? Object.keys(options).indexOf(initial) : 0;

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [hoverElementIndex, setHoverElementIndex] = useState<number>(initialIndex);

    const selectField = useRef<HTMLButtonElement>(null);
    const isPlaceholder = !valueKey || valueKey === "";
    const optionsKeys = options ? Object.keys(options) : "";

    const onToggle = () => {
        if (!disabled) setIsOpen(!isOpen);
    };

    const onChange = (newKey: string, index: number) => {
        if (!disabled) {
            handleChange(newKey);

            setHoverElementIndex(index);
            setIsOpen(false);
        }
    };

    const onKeyDown = (e: KeyboardEvent) => {
        e.preventDefault();
        if (!disabled) {
            switch (e.keyCode) {
                case 38:
                    if (hoverElementIndex > 0) setHoverElementIndex(hoverElementIndex - 1);
                    break;
                case 40:
                    if (hoverElementIndex < optionsKeys.length - 1) setHoverElementIndex(hoverElementIndex + 1);
                    break;
                case 13:
                    const newKey = optionsKeys[hoverElementIndex];
                    handleChange(newKey);

                    setIsOpen(false);
                    break;
            }
        }
    };

    return (
        <>
            <button
                ref={selectField}
                className={cx(styles.container, className, {
                    [styles.open]: isOpen,
                })}
                onFocus={() => {
                    if (!isOpen) setIsOpen(true);
                }}
                onKeyDown={onKeyDown}
                disabled={disabled}
            >
                <div
                    className={cx(styles.header, {
                        [styles.placeholder]: isPlaceholder,
                        [styles.disabled]: disabled,
                    })}
                    onMouseDown={onToggle}
                >
                    <span>{valueKey ? (options[valueKey] ? options[valueKey] : placeholder) : placeholder}</span>

                    <Icon className={cx(styles.icon, { [styles.active]: isOpen })} type={IconTypes.DOWN} size={20} />
                </div>

                {isOpen && (
                    <List
                        onChange={onChange}
                        setIsOpen={setIsOpen}
                        options={options}
                        selectField={selectField}
                        currentKey={valueKey}
                        hoverElementIndex={hoverElementIndex}
                    />
                )}
            </button>
        </>
    );
};

export default LogicStateSelect;
